/**
 * Price calculation utilities for product listings
 */

import type { Product, ProductVariant } from '../types/ProductListing'

export interface PriceRange {
  min: number
  max: number
  currency: string
  isSinglePrice: boolean
}

/**
 * Calculate price range from product variants
 * @param product - Product with optional variants
 * @returns Price range (min, max, currency) or null if no variants
 */
export function calculateVariantPriceRange(product: Product): PriceRange | null {
  // If product has no variants, return null (use product price instead)
  if (!product.variantCombinations || product.variantCombinations.length === 0) {
    return null
  }

  // Get active variant prices
  const variantPrices = product.variantCombinations
    .filter(variant => variant.price && variant.price.current > 0)
    .map(variant => variant.price.current)

  // If no valid variant prices, return null
  if (variantPrices.length === 0) {
    return null
  }

  const minPrice = Math.min(...variantPrices)
  const maxPrice = Math.max(...variantPrices)
  const currency = product.variantCombinations[0]?.price?.currency || product.price.currency

  return {
    min: minPrice,
    max: maxPrice,
    currency,
    isSinglePrice: minPrice === maxPrice
  }
}

/**
 * Get display price for a product (handles both single and variant products)
 * @param product - Product to get price for
 * @returns Object with current price, original price (if exists), and currency
 */
export function getProductDisplayPrice(product: Product): {
  current: number | PriceRange
  original?: number
  currency: string
  hasVariants: boolean
} {
  const variantPriceRange = calculateVariantPriceRange(product)

  if (variantPriceRange) {
    // Product has variants - return price range
    return {
      current: variantPriceRange,
      currency: variantPriceRange.currency,
      hasVariants: true
    }
  }

  // Single product - return product price
  return {
    current: product.price.current,
    original: product.price.original > product.price.current ? product.price.original : undefined,
    currency: product.price.currency,
    hasVariants: false
  }
}

/**
 * Format price range for display
 * @param priceRange - Price range object
 * @param formatter - Intl.NumberFormat instance
 * @returns Formatted string like "Rp 25,000 - Rp 50,000" or "Rp 25,000" if same
 */
export function formatPriceRange(
  priceRange: PriceRange,
  formatter: Intl.NumberFormat
): string {
  if (priceRange.isSinglePrice) {
    return formatter.format(priceRange.min)
  }

  return `${formatter.format(priceRange.min)} - ${formatter.format(priceRange.max)}`
}
